import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { format } from "date-fns";

// Estilos do PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 12,
    color: "#333",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingBottom: 20,
    marginBottom: 20,
  },
  logoContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  logoImage: {
    width: 200,
    height: "auto",
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#166534", // text-green-800 aproximado
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    marginTop: 4,
  },
  receiptInfo: {
    alignItems: "flex-end",
  },
  receiptTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  date: {
    fontSize: 10,
    color: "#666",
    marginTop: 4,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingBottom: 5,
    marginBottom: 10,
    color: "#166534",
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    width: 100,
    fontWeight: "bold",
    color: "#555",
  },
  value: {
    flex: 1,
  },
  table: {
    display: "table",
    width: "auto",
    marginTop: 10,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#eee",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#eee",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f9f9f9",
    padding: 5,
  },
  tableCol: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#eee",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableColHeader33: {
    width: "33.33%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#eee",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f9f9f9",
    padding: 5,
  },
  tableCol33: {
    width: "33.33%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#eee",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableCellHeader: {
    fontSize: 10,
    fontWeight: "bold",
  },
  tableCell: {
    fontSize: 10,
  },
  totalSection: {
    marginTop: 20,
    flexDirection: "row",
    justifyContent: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "#eee",
    paddingTop: 10,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: "bold",
    marginRight: 20,
  },
  totalValue: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#166534",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 10,
    color: "#888",
    borderTopWidth: 1,
    borderTopColor: "#eee",
    paddingTop: 10,
  },
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0
  );

const ReceiptPDF = ({ reservations, anonymizedPhone, generationDate }) => {
  if (!reservations || reservations.length === 0) return null;

  const mainReservation = reservations[0];
  const totalPaid = reservations.reduce(
    (acc, res) => acc + (res.totalPaid || 0),
    0
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <Image 
              src="/Logo%20timbo%20transparente%20branco.png" 
              style={styles.logoImage} 
            />
            <Text style={styles.title}>Ecoparque Timbó</Text>
            <Text style={styles.subtitle}>Comprovante de Reserva de Chalés</Text>
          </View>
          <View style={styles.receiptInfo}>
            <Text style={styles.receiptTitle}>RECIBO</Text>
            <Text style={styles.date}>
              Data de emissão: {format(generationDate, "dd/MM/yyyy HH:mm")}
            </Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Dados do Cliente</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Nome:</Text>
            <Text style={styles.value}>{mainReservation.guestInfo}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Telefone:</Text>
            <Text style={styles.value}>{anonymizedPhone}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Resumo das Reservas</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Chalé</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Check-in</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Check-out</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Valor Pago</Text>
              </View>
            </View>
            {reservations.map((res) => (
              <View style={styles.tableRow} key={res.reservationId || res.chaletNumber}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>Chalé {res.chaletNumber}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {res.checkInDate
                      ? format(res.checkInDate, "dd/MM/yyyy")
                      : "-"}
                  </Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {res.checkOutDate
                      ? format(res.checkOutDate, "dd/MM/yyyy")
                      : "-"}
                  </Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {formatCurrency(res.totalPaid)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Detalhamento de Pagamentos</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader33}>
                <Text style={styles.tableCellHeader}>Chalé</Text>
              </View>
              <View style={styles.tableColHeader33}>
                <Text style={styles.tableCellHeader}>Tipo</Text>
              </View>
              <View style={styles.tableColHeader33}>
                <Text style={styles.tableCellHeader}>Valor</Text>
              </View>
            </View>
            {reservations.flatMap((res) =>
              (res.payments || []).map((payment, index) => (
                <View
                  style={styles.tableRow}
                  key={`${res.reservationId}-${index}`}
                >
                  <View style={styles.tableCol33}>
                    <Text style={styles.tableCell}>Chalé {res.chaletNumber}</Text>
                  </View>
                  <View style={styles.tableCol33}>
                    <Text style={styles.tableCell}>{payment.type}</Text>
                  </View>
                  <View style={styles.tableCol33}>
                    <Text style={styles.tableCell}>
                      {formatCurrency(payment.value)}
                    </Text>
                  </View>
                </View>
              ))
            )}
          </View>
        </View>

        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>TOTAL GERAL PAGO:</Text>
          <Text style={styles.totalValue}>{formatCurrency(totalPaid)}</Text>
        </View>

        <View style={styles.footer}>
          <Text>
            Este documento é um comprovante interno gerado pelo sistema Ecoparque
            Timbó.
          </Text>
          <Text>Obrigado pela preferência!</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReceiptPDF;
